<template>
  <div
    class="modern-container"
    aria-label="Liste des tickets de support logistique"
    role="main"
  >
    <div class="tickets-header">
      <div class="header-content d-flex justify-content-between align-items-center">
        <h1 class="simple-title">Tickets de support</h1>
        <b-button
          v-if="user.role === 'supplier'"
          class="simple-create-btn"
          aria-label="Créer un nouveau ticket de support"
          @click="goToCreateTicket"
        >
          <i class="fas fa-plus mr-1" /> Nouveau ticket
        </b-button>
      </div>
    </div>
    <div class="modern-card">
      <div class="card-header">
        <div
          class="stats-row"
          aria-label="Statistiques des tickets de support"
        >
          <div class="stat-item">
            <div class="stat-icon-wrapper text-primary">
              <feather-icon icon="FileTextIcon" size="24" />
            </div>
            <span class="stat-number" aria-live="polite">{{ pagination.count || tickets.length }}</span>
            <span class="stat-label">Total tickets</span>
          </div>
          <div class="stat-item">
            <div class="stat-icon-wrapper text-warning">
              <feather-icon icon="ClockIcon" size="24" />
            </div>
            <span class="stat-number" aria-live="polite">{{ getOpenTicketsCount() }}</span>
            <span class="stat-label">Ouverts</span>
          </div>
          <div class="stat-item">
            <div class="stat-icon-wrapper text-info">
              <feather-icon icon="RefreshCwIcon" size="24" />
            </div>
            <span class="stat-number" aria-live="polite">{{ getInProgressTicketsCount() }}</span>
            <span class="stat-label">En cours</span>
          </div>
          <div class="stat-item">
            <div class="stat-icon-wrapper text-success">
              <feather-icon icon="CheckCircleIcon" size="24" />
            </div>
            <span class="stat-number" aria-live="polite">{{ getResolvedTicketsCount() }}</span>
            <span class="stat-label">Résolus</span>
          </div>
          <div class="stat-item">
            <div class="stat-icon-wrapper text-danger">
              <feather-icon icon="XCircleIcon" size="24" />
            </div>
            <span class="stat-number" aria-live="polite">{{ getClosedTicketsCount() }}</span>
            <span class="stat-label">Fermés</span>
          </div>
        </div>
        <div
          class="filter-bar mt-4 mb-2 px-0 px-md-3 py-2 d-flex flex-wrap align-items-center justify-content-between"
          style="background: #f8f9fa; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.04);"
        >
          <div class="d-flex flex-wrap align-items-center gap-3">
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-search text-muted mr-2" />
              <input
                v-model="search"
                type="text"
                class="form-control filter-input"
                placeholder="Rechercher par référence, titre, etc."
                aria-label="Recherche de ticket"
                style="min-width: 220px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @keyup.enter="applyFilters"
              >
            </div>
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-filter text-muted mr-2" />
              <select
                v-model="statusFilter"
                class="form-control filter-select"
                aria-label="Filtrer par statut"
                style="min-width: 170px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @change="applyFilters"
              >
                <option value="">
                  Tous les statuts
                </option>
                <option value="open">
                  Ouvert
                </option>
                <option value="in_progress">
                  En cours
                </option>
                <option value="resolved">
                  Résolu
                </option>
                <option value="closed">
                  Fermé
                </option>
              </select>
            </div>
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-calendar text-muted mr-2" />
              <select
                v-model="dateRangeFilter"
                class="form-control filter-select"
                aria-label="Filtrer par période"
                style="min-width: 170px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @change="applyFilters"
              >
                <option value="">
                  Toutes les dates
                </option>
                <option value="today">
                  Aujourd'hui
                </option>
                <option value="yesterday">
                  Hier
                </option>
                <option value="this_week">
                  Cette semaine
                </option>
                <option value="last_week">
                  Semaine dernière
                </option>
                <option value="this_month">
                  Ce mois
                </option>
                <option value="last_month">
                  Mois dernier
                </option>
                <option value="last_7_days">
                  7 derniers jours
                </option>
                <option value="last_30_days">
                  30 derniers jours
                </option>
              </select>
            </div>
            <button
              class="btn btn-outline-secondary ml-2"
              aria-label="Réinitialiser les filtres"
              style="border-radius: 8px;"
              @click="resetFilters"
            >
              <i class="fas fa-times mr-1" /> Réinitialiser
            </button>
          </div>
          <div
            v-if="search || statusFilter || dateRangeFilter"
            class="applied-filters mt-2 d-flex flex-wrap align-items-center gap-2"
          >
            <span
              v-if="search"
              class="applied-filter-chip"
            >
              <i class="fas fa-search mr-1" />
              <span>{{ search }}</span>
              <button
                class="clear-chip-btn ml-1"
                aria-label="Effacer la recherche"
                @click="search = ''; applyFilters()"
              >&times;</button>
            </span>
            <span
              v-if="statusFilter"
              class="applied-filter-chip"
            >
              <i class="fas fa-filter mr-1" />
              <span>{{ statusFilterLabel }}</span>
              <button
                class="clear-chip-btn ml-1"
                aria-label="Effacer le filtre de statut"
                @click="statusFilter = ''; applyFilters()"
              >&times;</button>
            </span>
            <span
              v-if="dateRangeFilter"
              class="applied-filter-chip"
            >
              <i class="fas fa-calendar mr-1" />
              <span>{{ dateRangeFilterLabel }}</span>
              <button
                class="clear-chip-btn ml-1"
                aria-label="Effacer le filtre de date"
                @click="dateRangeFilter = ''; applyFilters()"
              >&times;</button>
            </span>
            <span
              v-if="search || statusFilter || dateRangeFilter"
              class="ml-2"
            >
              <button
                class="btn btn-link p-0 applied-filters-clear-all"
                aria-label="Effacer tous les filtres"
                @click="resetFilters"
              >
                <i class="fas fa-times-circle mr-1" /> Effacer tous les filtres
              </button>
            </span>
          </div>
        </div>
        <div
          class="sr-only"
          aria-live="polite"
        >
          Utilisez les filtres et la recherche pour affiner la liste des tickets de support. Les colonnes du tableau sont accessibles au clavier et compatibles avec les lecteurs d'écran.
        </div>
      </div>

      <div class="modern-table-container">
        <template v-if="loading">
          <div class="loading-state">
            <b-spinner
              variant="danger"
              label="Chargement des tickets..."
              style="width: 3rem; height: 3rem;"
              class="mb-3"
            />
            <span>Chargement des tickets...</span>
          </div>
        </template>
        <template v-else-if="tickets.length === 0">
          <div class="text-center py-5">
            <div
              class="no-results-card shadow-sm enhanced-empty-state"
              style="background: #fff; border-radius: 20px; padding: 2.5rem 2rem; max-width: 520px; margin: 0 auto; box-shadow: 0 8px 32px rgba(156,163,175,0.10);"
            >
              <div class="mb-4">
                <img
                  src="/public/img/icons/empty-support.svg"
                  alt="Aucun ticket"
                  style="height: 110px; opacity: 0.85;"
                  onerror="this.style.display='none'"
                >
                <i
                  v-if="false"
                  class="fas fa-inbox fa-4x text-muted"
                />
              </div>
              <h3
                class="text-muted mb-2 font-weight-bold"
                style="font-size: 1.5rem;"
              >
                Aucun ticket à afficher
              </h3>
              <p
                class="text-muted mb-4"
                style="font-size: 1.08rem;"
              >
                <span v-if="search || statusFilter">
                  <span style="font-weight: 500;">Aucun ticket ne correspond à vos critères.</span><br>
                  Essayez d'élargir votre recherche ou de réinitialiser les filtres pour voir plus de résultats.
                </span>
                <span v-else>
                  <span style="font-weight: 500;">Vous n'avez pas encore de tickets de support.</span><br>
                  Créez votre premier ticket pour obtenir de l'aide ou signaler un problème à notre équipe.
                </span>
              </p>
              <div v-if="search || statusFilter">
                <b-button
                  variant="outline-primary"
                  size="lg"
                  class="mt-1 px-4"
                  @click="resetFilters"
                >
                  <i class="fas fa-sync-alt mr-1" /> Réinitialiser les filtres
                </b-button>
              </div>
              <div v-else-if="user.role === 'supplier'">
                <b-button
                  variant="primary"
                  size="lg"
                  class="mt-1 px-4"
                  @click="goToCreateTicket"
                >
                  <i class="fas fa-plus mr-1" /> Créer un ticket de support
                </b-button>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <b-table
            :items="tickets"
            :fields="fields"
            responsive="sm"
            class="modern-table"
            hover
            borderless
            aria-label="Tableau des tickets de support logistique"
            caption-top
          >
            <!-- Reference -->
            <template #cell(reference)="{ item }">
              <div class="reference-cell">
                <span
                  v-b-tooltip.hover
                  class="reference-badge"
                  title="Cliquer pour copier la référence"
                  @click="copyReference(item.reference)"
                >
                  {{ item.reference || '-' }}
                </span>
              </div>
            </template>
            <!-- Title -->
            <template #cell(title)="{ item }">
              <div class="title-cell">
                <span class="title-text">{{ item.title }}</span>
              </div>
            </template>
            <!-- Category -->
            <template #cell(category)="{ item }">
              <div class="category-cell">
                <span
                  v-if="item.category"
                  v-b-tooltip.hover
                  class="category-tag"
                  :title="item.category.description || item.category.display_name || item.category.name"
                >
                  <i class="fas fa-tag" />
                  {{ item.category.display_name || item.category.name }}
                </span>
                <span
                  v-else
                  class="empty-value"
                >-</span>
              </div>
            </template>
            <!-- Status -->
            <template #cell(status)="{ item }">
              <div class="status-cell">
                <span
                  :class="['status-pill', getStatusClass(item.status)]"
                >
                  <i :class="getStatusIcon(item.status)" />
                  {{ item.status ? (item.status.display_name || item.status.name) : '-' }}
                </span>
              </div>
            </template>
            <!-- Orders Count -->
            <template #cell(orders)="{ item }">
              <div class="orders-cell">
                <span class="orders-count">
                  {{ item.orders_count || 0 }}
                </span>
              </div>
            </template>
            <!-- Order IDs -->
            <template #cell(order_ids)="{ item }">
              <div class="order-ids-cell">
                <div
                  v-if="item.order_ids && item.order_ids.length"
                  class="order-tags"
                >
                  <span
                    v-for="oid in item.order_ids.slice(0, 3)"
                    :key="oid"
                    class="order-tag"
                  >
                    #{{ oid }}
                  </span>
                  <span
                    v-if="item.order_ids.length > 3"
                    class="more-orders"
                  >
                    +{{ item.order_ids.length - 3 }}
                  </span>
                </div>
                <span
                  v-else
                  class="empty-value"
                >-</span>
              </div>
            </template>
            <!-- Submitter -->
            <template #cell(submitter)="{ item }">
              <div class="user-cell">
                <div
                  v-if="item.submitter && item.submitter.username"
                  class="user-info-simple"
                >
                  <span class="user-name">{{ item.submitter.username }}</span>
                </div>
                <span
                  v-else
                  class="empty-value"
                >-</span>
              </div>
            </template>
            <!-- Assignee -->
            <template #cell(assignee)="{ item }">
              <div class="user-cell">
                <div
                  v-if="item.assignee && item.assignee.username"
                  class="user-info"
                >
                  <div class="user-avatar">
                    {{ item.assignee.username.charAt(0).toUpperCase() }}
                  </div>
                  <span class="user-name">{{ item.assignee.username }}</span>
                </div>
                <span
                  v-else
                  class="empty-value"
                >-</span>
              </div>
            </template>
            <!-- Created At -->
            <template #cell(created_at)="{ item }">
              <div class="date-cell">
                <span class="date-text">{{ formatDate(item.created_at) }}</span>
              </div>
            </template>
            <!-- Actions -->
            <template #cell(actions)="{ item }">
              <div class="actions-cell">
                <button
                  v-b-tooltip.hover
                  class="action-btn view-btn"
                  title="Voir le ticket"
                  @click="goToDetail(item.id)"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                    />
                  </svg>
                </button>
                <button
                  v-if="user.role === 'supplier' && item.submitter && item.submitter.id === user.id && item.status && item.status.is_default"
                  v-b-tooltip.hover
                  class="action-btn edit-btn"
                  title="Modifier le ticket"
                  @click="goToEdit(item.id)"
                >
                  <feather-icon
                    icon="Edit3Icon"
                    size="17"
                  />
                </button>
                <button
                  v-if="isAdmin"
                  v-b-tooltip.hover
                  :class="['action-btn', 'return-btn', { 'btn-disabled': !isTicketEligibleForReturn(item) }]"
                  :title="isTicketEligibleForReturn(item) ? 'Remettre la commande en cours' : 'Action disponible uniquement pour les tickets Ouverts ou En cours'"
                  @click="handleReturnOrderClick(item)"
                >
                  <feather-icon
                    icon="RotateCcwIcon"
                    size="16"
                  />
                </button>
              </div>
            </template>
          </b-table>

          <!-- Enhanced Pagination Controls -->
          <div
            v-if="pagination.count > pagination.page_size"
            class="pagination-container d-flex justify-content-between align-items-center mt-4 px-3"
          >
            <div class="pagination-info">
              <span class="text-muted">
                Affichage de {{ getDisplayRange() }} sur {{ pagination.count }} tickets
              </span>
              <div class="page-size-selector ml-3 d-inline-block">
                <label class="text-muted mr-2">Items par page:</label>
                <select
                  v-model="pageSize"
                  class="form-control d-inline-block"
                  style="width: auto; font-size: 0.875rem;"
                  @change="changePageSize"
                >
                  <option value="10">
                    10
                  </option>
                  <option value="20">
                    20
                  </option>
                  <option value="50">
                    50
                  </option>
                  <option value="100">
                    100
                  </option>
                </select>
              </div>
            </div>
            <b-pagination
              v-model="currentPage"
              :total-rows="pagination.count"
              :per-page="pagination.page_size"
              :limit="5"
              size="md"
              class="mb-0"
              @change="onPageChange"
            />
          </div>
        </template>
      </div>
    </div>

    <!-- Modal: Remettre la commande en cours -->
    <b-modal
      v-model="showReturnOrderModal"
      title="Remettre la commande en cours"
      header-bg-variant="primary"
      header-text-variant="white"
      size="lg"
      centered
      no-close-on-backdrop
    >
      <div v-if="selectedTicket">
        <!-- Ticket Info Header -->
        <div class="p-2 mb-3 rounded bg-light border d-flex justify-content-between align-items-center">
          <div>
            <strong>Ticket :</strong>
            <span class="ml-1 text-primary font-weight-bold">#{{ selectedTicket.reference || selectedTicket.id }}</span>
            <span
              v-if="selectedTicket.title"
              class="ml-2 text-muted"
            >- {{ selectedTicket.title }}</span>
          </div>
          <div>
            <span
              :class="['status-pill py-1 px-2', getStatusClass(selectedTicket.status)]"
              style="font-size: 0.75rem;"
            >
              {{ selectedTicket.status ? (selectedTicket.status.display_name || selectedTicket.status.name) : '-' }}
            </span>
          </div>
        </div>

        <!-- Security Guard Alert if Ticket is NOT Open or In Progress -->
        <b-alert
          v-if="!isTicketEligibleForReturn(selectedTicket)"
          variant="danger"
          show
          class="d-flex align-items-center mb-3"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            class="mr-2"
            size="20"
          />
          <div>
            <strong>Opération non autorisée :</strong> Seuls les tickets ayant le statut <strong>Ouvert</strong> ou <strong>En cours</strong> peuvent être traités pour remettre leurs commandes en cours.
          </div>
        </b-alert>

        <!-- Loading Orders State -->
        <div
          v-if="loadingOrders"
          class="text-center py-4"
        >
          <b-spinner
            variant="primary"
            class="mb-2"
          />
          <p class="text-muted mb-0">
            Vérification des statuts des commandes liées...
          </p>
        </div>

        <!-- Orders Content -->
        <div v-else-if="ticketOrders.length > 0">
          <b-alert
            v-if="eligibleOrdersCount > 0"
            variant="info"
            show
            class="mb-3"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="InfoIcon"
                class="mr-2"
                size="18"
              />
              <span>
                Seules les commandes ayant actuellement le statut <strong>Retour temporaire</strong> ou <strong>Livré temporaire</strong> sont éligibles pour être remises au statut <strong>En cours</strong>.
              </span>
            </div>
          </b-alert>

          <b-alert
            v-else
            variant="warning"
            show
            class="mb-3"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="AlertCircleIcon"
                class="mr-2"
                size="18"
              />
              <span>
                <strong>Aucune commande éligible :</strong> Aucune commande associée à ce ticket n'a le statut <em>Retour temporaire</em> ou <em>Livré temporaire</em>.
              </span>
            </div>
          </b-alert>

          <!-- Table of Orders -->
          <div class="table-responsive border rounded mb-3">
            <table
              class="table table-hover mb-0"
              style="font-size: 0.9rem;"
            >
              <thead class="thead-light">
                <tr>
                  <th>N° Commande</th>
                  <th>Client</th>
                  <th>Téléphone</th>
                  <th>Montant TTC</th>
                  <th>Statut Actuel</th>
                  <th class="text-center">
                    Éligibilité
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="order in ticketOrders"
                  :key="order.id"
                >
                  <td><strong>#{{ order.id }}</strong></td>
                  <td>{{ order.name || '-' }}</td>
                  <td>{{ order.phone1 || order.phone || '-' }}</td>
                  <td>{{ formatOrderMoney(order.price_ttc) }}</td>
                  <td>
                    <b-badge :variant="getOrderStatusBadgeVariant(order.status)">
                      {{ getOrderStatusLabel(order.status) }}
                    </b-badge>
                  </td>
                  <td class="text-center">
                    <span
                      v-if="isOrderEligible(order)"
                      class="badge badge-success px-2 py-1"
                    >
                      <feather-icon
                        icon="CheckIcon"
                        size="14"
                        class="mr-1"
                      />
                      Éligible &rarr; En cours
                    </span>
                    <span
                      v-else
                      v-b-tooltip.hover
                      class="badge badge-secondary px-2 py-1"
                      title="Statut incompatible avec cette opération"
                    >
                      <feather-icon
                        icon="XIcon"
                        size="14"
                        class="mr-1"
                      />
                      Non éligible
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="eligibleOrdersCount > 0"
            class="text-dark font-weight-bold"
          >
            <feather-icon
              icon="CheckCircleIcon"
              class="text-success mr-1"
              size="16"
            />
            {{ eligibleOrdersCount }} commande(s) sera(seront) remise(s) au statut <span class="badge badge-primary">En cours</span>.
          </div>
        </div>

        <!-- Empty Orders State -->
        <div
          v-else
          class="text-center py-4 text-muted"
        >
          <feather-icon
            icon="InboxIcon"
            size="36"
            class="mb-2 text-muted"
          />
          <p class="mb-0">
            Aucune commande liée à ce ticket.
          </p>
        </div>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="isSubmittingReturn"
          @click="showReturnOrderModal = false"
        >
          Annuler
        </b-button>
        <b-button
          v-if="isTicketEligibleForReturn(selectedTicket) && eligibleOrdersCount > 0"
          variant="primary"
          :disabled="isSubmittingReturn || loadingOrders"
          @click="confirmReturnOrdersToEnCours"
        >
          <b-spinner
            v-if="isSubmittingReturn"
            small
            class="mr-1"
          />
          <feather-icon
            v-else
            icon="RotateCcwIcon"
            class="mr-1"
            size="16"
          />
          Remettre en cours ({{ eligibleOrdersCount }})
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import storeAuth from '@/store/store'
import { getOrderStatusLabel } from '@/core/utils/orderStatus'
import {
  BButton, BTable, BPagination, BModal, BSpinner, BBadge, BAlert,
} from 'bootstrap-vue'

export default {
  components: {
    BButton,
    BTable,
    BPagination,
    BModal,
    BSpinner,
    BBadge,
    BAlert,
  },
  data() {
    return {
      tickets: [],
      loading: false,
      fields: [
        { key: 'reference', label: 'Référence', sortable: true },
        { key: 'title', label: 'Titre', sortable: true },
        { key: 'category', label: 'Catégorie', sortable: true },
        { key: 'status', label: 'Statut', sortable: true },
        { key: 'orders', label: 'Nb. Commandes', sortable: false },
        { key: 'order_ids', label: 'BL Commandes', sortable: false },
        { key: 'assignee', label: 'Assigné à', sortable: true },
        { key: 'created_at', label: 'Créé le', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      user: {},
      search: '',
      statusFilter: '',
      dateRangeFilter: '',
      currentPage: 1,
      pageSize: 20,
      pagination: {
        count: 0,
        next: null,
        previous: null,
        page_size: 20,
        current_page: 1,
        total_pages: 1,
      },
      showReturnOrderModal: false,
      selectedTicket: null,
      ticketOrders: [],
      loadingOrders: false,
      isSubmittingReturn: false,
    }
  },
  computed: {
    isAdmin() {
      return ['admin', 'superadmin', 'admin_developer'].includes(this.user && this.user.role)
    },
    eligibleOrdersCount() {
      return this.ticketOrders.filter(o => this.isOrderEligible(o)).length
    },
    statusFilterLabel() {
      switch (this.statusFilter) {
        case 'open': return 'Ouvert'
        case 'in_progress': return 'En cours'
        case 'resolved': return 'Résolu'
        case 'closed': return 'Fermé'
        default: return this.statusFilter
      }
    },
    dateRangeFilterLabel() {
      switch (this.dateRangeFilter) {
        case 'today': return 'Aujourd\'hui'
        case 'yesterday': return 'Hier'
        case 'this_week': return 'Cette semaine'
        case 'last_week': return 'Semaine dernière'
        case 'this_month': return 'Ce mois'
        case 'last_month': return 'Mois dernier'
        case 'last_7_days': return '7 derniers jours'
        case 'last_30_days': return '30 derniers jours'
        default: return this.dateRangeFilter
      }
    },
  },
  created() {
    this.user = storeAuth.state.user
    // Dynamically adjust columns based on user role
    if (this.user.role !== 'supplier') {
      // Insert 'submitter' column before 'assignee'
      const idx = this.fields.findIndex(f => f.key === 'assignee')
      if (idx !== -1) {
        this.fields.splice(idx, 0, { key: 'submitter', label: 'Demandeur', sortable: true })
      }
    }
  },
  mounted() {
    this.fetchTickets()
  },
  methods: {
    async fetchTickets(params = {}) {
      this.loading = true
      try {
        const queryParams = {
          page: this.currentPage,
          page_size: this.pageSize,
          ...params,
        }
        if (this.search) queryParams.search = this.search
        if (this.statusFilter) queryParams.status_name = this.statusFilter
        if (this.dateRangeFilter) queryParams.date_range = this.dateRangeFilter

        const { data } = await axios.get('/api/support/tickets/', { params: queryParams })
        this.tickets = data.results || data
        this.pagination = {
          count: data.count || 0,
          next: data.next,
          previous: data.previous,
          page_size: data.page_size || this.pageSize,
          current_page: data.current_page || this.currentPage,
          total_pages: data.total_pages || 1,
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching tickets:', error)
        this.$bvToast.toast('Erreur lors du chargement des tickets', { variant: 'danger' })
      } finally {
        this.loading = false
      }
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchTickets()
    },
    changePageSize() {
      this.currentPage = 1
      this.fetchTickets()
    },
    getDisplayRange() {
      const start = (this.currentPage - 1) * this.pagination.page_size + 1
      const end = Math.min(this.currentPage * this.pagination.page_size, this.pagination.count)
      return `${start}-${end}`
    },
    getOpenTicketsCount() {
      return this.tickets.filter(t => {
        if (!t.status) return false
        const statusName = (t.status.name || '').toLowerCase()
        const displayName = (t.status.display_name || '').toLowerCase()
        return statusName === 'ouvert' || displayName === 'ouvert'
               || statusName.includes('open') || displayName.includes('open')
      }).length
    },
    getInProgressTicketsCount() {
      return this.tickets.filter(t => {
        if (!t.status) return false
        const statusName = (t.status.name || '').toLowerCase()
        const displayName = (t.status.display_name || '').toLowerCase()
        return statusName === 'en cours' || displayName === 'en cours'
               || statusName === 'en_cours' || displayName === 'en_cours'
               || statusName.includes('in progress') || displayName.includes('in progress')
               || statusName.includes('progress') || displayName.includes('progress')
      }).length
    },
    getResolvedTicketsCount() {
      return this.tickets.filter(t => {
        if (!t.status) return false
        const statusName = (t.status.name || '').toLowerCase()
        const displayName = (t.status.display_name || '').toLowerCase()
        return statusName === 'résolu' || displayName === 'résolu'
               || statusName === 'resolu' || displayName === 'resolu'
               || statusName.includes('resolved') || displayName.includes('resolved')
      }).length
    },
    getClosedTicketsCount() {
      return this.tickets.filter(t => {
        if (!t.status) return false
        const statusName = (t.status.name || '').toLowerCase()
        const displayName = (t.status.display_name || '').toLowerCase()

        // Only count as closed if explicitly "fermé" or has is_closed=true BUT not resolved
        const isExplicitlyClosed = statusName === 'fermé' || displayName === 'fermé'
                                  || statusName === 'ferme' || displayName === 'ferme'
                                  || statusName.includes('closed') || displayName.includes('closed')

        const isResolvedStatus = statusName === 'résolu' || displayName === 'résolu'
                                || statusName === 'resolu' || displayName === 'resolu'
                                || statusName.includes('resolved') || displayName.includes('resolved')

        // Don't count resolved tickets as closed, even if is_closed is true
        if (isResolvedStatus) return false

        return isExplicitlyClosed || t.status.is_closed === true
      }).length
    },
    getStatusClass(status) {
      if (!status) return 'status-open'

      const statusName = (status.name || '').toLowerCase()
      const displayName = (status.display_name || '').toLowerCase()

      // Check for in progress status
      if (statusName === 'en cours' || displayName === 'en cours'
          || statusName === 'en_cours' || displayName === 'en_cours'
          || statusName.includes('in progress') || displayName.includes('in progress')
          || statusName.includes('progress') || displayName.includes('progress')) {
        return 'status-in-progress'
      }

      // Check for resolved status
      if (statusName === 'résolu' || displayName === 'résolu'
          || statusName === 'resolu' || displayName === 'resolu'
          || statusName.includes('resolved') || displayName.includes('resolved')) {
        return 'status-resolved'
      }

      // Check for closed status
      if (statusName === 'fermé' || displayName === 'fermé'
          || statusName === 'ferme' || displayName === 'ferme'
          || statusName.includes('closed') || displayName.includes('closed')
          || status.is_closed === true) {
        return 'status-closed'
      }

      // Default to open
      return 'status-open'
    },
    getStatusIcon(status) {
      if (!status) return 'fas fa-clock'

      const statusName = (status.name || '').toLowerCase()
      const displayName = (status.display_name || '').toLowerCase()

      // Check for in progress status
      if (statusName === 'en cours' || displayName === 'en cours'
          || statusName === 'en_cours' || displayName === 'en_cours'
          || statusName.includes('in progress') || displayName.includes('in progress')
          || statusName.includes('progress') || displayName.includes('progress')) {
        return 'fas fa-spinner'
      }

      // Check for resolved status
      if (statusName === 'résolu' || displayName === 'résolu'
          || statusName === 'resolu' || displayName === 'resolu'
          || statusName.includes('resolved') || displayName.includes('resolved')) {
        return 'fas fa-check'
      }

      // Check for closed status
      if (statusName === 'fermé' || displayName === 'fermé'
          || statusName === 'ferme' || displayName === 'ferme'
          || statusName.includes('closed') || displayName.includes('closed')
          || status.is_closed === true) {
        return 'fas fa-check-circle'
      }

      // Default to open
      return 'fas fa-clock'
    },
    goToDetail(id) {
      const ticket = this.tickets.find(t => t.id === id)
      if (this.user.role === 'supplier' && ticket && ticket.submitter && ticket.submitter.id !== this.user.id) {
        this.$bvToast.toast('Vous n\'avez pas la permission de voir ce ticket.', { variant: 'danger' })
        return
      }
      this.$router.push({ name: 'SupportTicketDetail', params: { id } })
    },
    goToCreateTicket() {
      this.$router.push({ name: 'SupportTicketCreate' })
    },
    goToEdit(id) {
      const ticket = this.tickets.find(t => t.id === id)
      if (this.user.role === 'supplier' && ticket && ticket.submitter && ticket.submitter.id !== this.user.id) {
        this.$bvToast.toast('Vous n\'avez pas la permission de modifier ce ticket.', { variant: 'danger' })
        return
      }
      this.$router.push({ name: 'SupportTicketEdit', params: { id } })
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    copyReference(reference) {
      if (!reference) return

      navigator.clipboard.writeText(reference).then(() => {
        this.$bvToast.toast(`Référence ${reference} copiée!`, {
          variant: 'success',
          autoHideDelay: 2000,
        })
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = reference
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)

        this.$bvToast.toast(`Référence ${reference} copiée!`, {
          variant: 'success',
          autoHideDelay: 2000,
        })
      })
    },
    isTicketEligibleForReturn(ticket) {
      if (!ticket || !ticket.status) return false
      const statusName = (ticket.status.name || '').toLowerCase()
      const displayName = (ticket.status.display_name || '').toLowerCase()

      const isOpen = statusName === 'ouvert' || displayName === 'ouvert'
             || statusName === 'open' || displayName === 'open'
             || statusName.includes('ouvert') || displayName.includes('ouvert')
             || statusName.includes('open') || displayName.includes('open')

      const isInProgress = statusName === 'en cours' || displayName === 'en cours'
             || statusName === 'en_cours' || displayName === 'en_cours'
             || statusName.includes('in progress') || displayName.includes('in progress')
             || statusName.includes('progress') || displayName.includes('progress')

      return isOpen || isInProgress
    },
    isTicketOpen(ticket) {
      return this.isTicketEligibleForReturn(ticket)
    },
    isOrderEligible(order) {
      if (!order) return false
      const statusCode = Number(order.status)
      if (statusCode === 19 || statusCode === 20) {
        return true
      }
      const statusStr = (order.status_display || order.status_name || (typeof order.status === 'string' ? order.status : '')).toLowerCase()
      return statusStr.includes('retour temporaire') || statusStr.includes('livré temporaire') || statusStr.includes('livre temporaire')
    },
    getOrderStatusLabel(status) {
      return getOrderStatusLabel(status)
    },
    getOrderStatusBadgeVariant(status) {
      const code = Number(status)
      switch (code) {
        case 3: return 'primary'
        case 19: return 'warning'
        case 20: return 'danger'
        case 4: return 'success'
        case 6: return 'success'
        default: return 'secondary'
      }
    },
    formatOrderMoney(amount) {
      if (amount == null || amount === undefined) return '-'
      return `${Number(amount).toFixed(3)} TND`
    },
    async handleReturnOrderClick(ticket) {
      if (!this.isAdmin) {
        this.$bvToast.toast('Cette action est réservée aux administrateurs.', { variant: 'danger' })
        return
      }
      if (!this.isTicketEligibleForReturn(ticket)) {
        this.$bvToast.toast('Cette action ne peut être effectuée que pour les tickets au statut "Ouvert" ou "En cours".', {
          variant: 'warning',
          title: 'Action non autorisée',
        })
        return
      }
      this.selectedTicket = ticket
      this.ticketOrders = []
      this.showReturnOrderModal = true
      this.loadingOrders = true

      try {
        const { data: ticketData } = await axios.get(`/api/support/tickets/${ticket.id}/`)
        let orders = ticketData.orders || []

        if ((!orders || orders.length === 0) && (ticket.order_ids && ticket.order_ids.length > 0)) {
          const orderPromises = ticket.order_ids.map(oid => axios.get(`/api/orders/detail/${oid}`).then(res => res.data).catch(() => null))
          const fetchedOrders = await Promise.all(orderPromises)
          orders = fetchedOrders.filter(Boolean)
        } else if (orders.length > 0) {
          const refreshedPromises = orders.map(ord => axios.get(`/api/orders/detail/${ord.id}`).then(res => res.data).catch(() => ord))
          orders = await Promise.all(refreshedPromises)
        }

        this.ticketOrders = orders
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading ticket orders:', error)
        this.$bvToast.toast('Erreur lors du chargement des commandes liées.', { variant: 'danger' })
      } finally {
        this.loadingOrders = false
      }
    },
    async confirmReturnOrdersToEnCours() {
      if (!this.isAdmin) {
        this.$bvToast.toast('Action non autorisée.', { variant: 'danger' })
        return
      }
      if (!this.isTicketEligibleForReturn(this.selectedTicket)) {
        this.$bvToast.toast('Le ticket doit être au statut "Ouvert" ou "En cours".', { variant: 'warning' })
        return
      }

      const eligibleOrders = this.ticketOrders.filter(o => this.isOrderEligible(o))
      if (eligibleOrders.length === 0) {
        this.$bvToast.toast('Aucune commande éligible à remettre en cours.', { variant: 'warning' })
        return
      }

      this.isSubmittingReturn = true

      try {
        const updatePromises = eligibleOrders.map(order => axios.put(`/api/orders/quick-update/${order.id}/`, { status: 3 })
          .then(() => ({ success: true, id: order.id }))
          .catch(err => {
            // eslint-disable-next-line no-console
            console.error(`Error updating order ${order.id}:`, err)
            return { success: false, id: order.id }
          }))

        const results = await Promise.all(updatePromises)
        const successCount = results.filter(r => r.success).length
        const errorCount = results.filter(r => !r.success).length

        if (successCount > 0) {
          this.$bvToast.toast(
            successCount === 1
              ? `La commande #${eligibleOrders[0].id} a été remise au statut 'En cours' avec succès.`
              : `${successCount} commande(s) remise(s) au statut 'En cours' avec succès.`,
            {
              variant: 'success',
              title: 'Succès',
              autoHideDelay: 3500,
            },
          )
        }

        if (errorCount > 0) {
          this.$bvToast.toast(`Échec de la mise à jour pour ${errorCount} commande(s).`, {
            variant: 'danger',
            title: 'Erreur',
          })
        }

        this.showReturnOrderModal = false
        this.fetchTickets()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error returning orders to en cours:', err)
        this.$bvToast.toast('Une erreur est survenue lors de l\'opération.', { variant: 'danger' })
      } finally {
        this.isSubmittingReturn = false
      }
    },
    applyFilters() {
      this.currentPage = 1
      this.fetchTickets()
    },
    resetFilters() {
      this.search = ''
      this.statusFilter = ''
      this.dateRangeFilter = ''
      this.currentPage = 1
      this.applyFilters()
    },
  },
}
</script>

<style lang="scss" scoped>
.modern-container {
  padding: 1.5rem;
  animation: slideUp 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.tickets-header {
  background: #ffffff;
  padding: 2.5rem 2rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  border: 1px solid #e2e8f0;
  color: #0A2634;
  position: relative;
  overflow: hidden;
}

.simple-title {
  font-weight: 700;
  margin: 0;
  color: #0A2634;
  position: relative;
  z-index: 1;
  font-size: 1.8rem;
  letter-spacing: 0.5px;
}

.simple-create-btn {
  background: #0A2634;
  color: #ffffff;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  transition: all 0.2s ease;
  z-index: 1;
  position: relative;

  &:hover {
    background: #12384a;
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;

  .stat-item {
    flex: 1;
    min-width: 150px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    padding: 1.5rem;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    border: 1px solid rgba(0, 0, 0, 0.03);
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(90deg, #B50100, #ff4d4d);
      transform: scaleX(0);
      transition: transform 0.3s ease;
      transform-origin: left;
    }

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 12px 40px rgba(181, 1, 0, 0.12);

      &::after {
        transform: scaleX(1);
      }
    }

    .stat-icon-wrapper {
      margin-bottom: 0.75rem;
      padding: 0.75rem;
      border-radius: 50%;
      background: rgba(0,0,0,0.03);
    }

    .stat-number {
      font-size: 2.2rem;
      font-weight: 800;
      color: #090909;
      margin-bottom: 0.2rem;
      line-height: 1;
    }
    .stat-label {
      color: #6e6b7b;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-size: 0.85rem;
    }
  }
}

.modern-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  background: white;
  overflow: hidden;
}

.filter-bar {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(0,0,0,0.03);
}

.filter-select, .filter-input {
  border: 1px solid #e0e0e0 !important;
  border-radius: 8px !important;
  background-color: white;
  padding: 0.5rem 1rem;
  transition: all 0.2s;
  &:focus {
    border-color: #B50100 !important;
    box-shadow: 0 0 0 0.2rem rgba(181, 1, 0, 0.25) !important;
    outline: none;
  }
}

::v-deep .table-hover tbody tr {
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(181, 1, 0, 0.03) !important;
    transform: scale(1.005);
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
    border-radius: 8px;
  }
}

/* Pagination Styles */
.pagination-container {
  border-top: 1px solid #f1f5f9;
  padding-top: 1rem;
  background: #f8f9fa;
  border-radius: 0 0 20px 20px;
}

.pagination-info {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

::v-deep .pagination .page-link {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-weight: 500;
  margin: 0 2px;
}

::v-deep .pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #B50100 0%, #ff4d4d 100%);
  border-color: #B50100;
  color: white;
}

::v-deep .pagination .page-link:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  color: #1e293b;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}

/* Cell Styles */
.reference-cell {
  display: flex;
  align-items: center;
}
.reference-badge {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #000000;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.reference-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(181, 1, 0, 0.15);
  background: linear-gradient(135deg, #B50100 0%, #ff4d4d 100%);
  color: white;
}
.title-cell {
  max-width: 200px;
}
.title-text {
  font-weight: 600;
  color: #1e293b;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.category-cell {
  display: flex;
  align-items: center;
}
.category-tag {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #000000;
  font-size: 1rem;
  font-weight: 700;
  cursor: help;
}
.category-tag i {
  color: #64748b;
  font-size: 0.75rem;
}
.status-cell {
  display: flex;
  align-items: center;
}
.status-pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
.status-open {
  background: linear-gradient(135deg, #ff9f43 0%, #ff8c00 100%);
  color: white;
}
.status-in-progress {
  background: rgba(91, 166, 227, 0.68);
  color: white;
}
.status-resolved {
  background: #2FDD6F;
  color: white;
}
.status-closed {
  background: linear-gradient(135deg, #090909 0%, #1a202c 100%);
  color: white;
}
.orders-cell {
  display: flex;
  justify-content: center;
}
.orders-count {
  background: linear-gradient(135deg, #ffb976 0%, #ff9f43 100%);
  color: #2d3748;
  padding: 0.5rem 0.875rem;
  border-radius: 15px;
  font-weight: 600;
  font-size: 0.875rem;
  min-width: 2rem;
  text-align: center;
}
.order-ids-cell {
  max-width: 150px;
}
.order-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.order-tag {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 500;
}
.more-orders {
  background: #e2e8f0;
  color: #64748b;
  padding: 0.25rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 500;
}
.user-cell {
  display: flex;
  align-items: center;
}
.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.user-info-simple {
  display: flex;
  align-items: center;
}
.user-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #B50100 0%, #ff4d4d 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
}
.user-name {
  font-weight: 500;
  color: #1e293b;
}
.date-cell {
  color: #64748b;
  font-size: 0.875rem;
}
.actions-cell {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}
.action-btn {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}
.view-btn {
  background: transparent;
  color: #64748b;
}
.view-btn:hover {
  transform: scale(1.1);
  color: #0A2634;
}
.edit-btn {
  background: #e1effe;
  color: #0A2634;
  border: 1px solid #b7d5f4;
}
.edit-btn:hover {
  transform: scale(1.1);
  background: #d7e9fb;
  box-shadow: 0 4px 12px rgba(10, 38, 52, 0.16);
}
.return-btn {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;

  &:hover:not(.btn-disabled) {
    transform: scale(1.1);
    background: #dcfce7;
    color: #15803d;
    box-shadow: 0 4px 12px rgba(22, 163, 74, 0.2);
  }

  &.btn-disabled {
    opacity: 0.35;
    cursor: not-allowed;
    background: #f1f5f9;
    color: #94a3b8;
    border-color: #e2e8f0;

    &:hover {
      transform: none;
    }
  }
}
.empty-value {
  color: #9ca3af;
  font-style: italic;
}
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  color: #64748b;
}
.loading-spinner {
  font-size: 2rem;
  color: #ff9f43;
}
.applied-filters {
  margin-top: 0.5rem;
  gap: 0.5rem;
}
.applied-filter-chip {
  display: inline-flex;
  align-items: center;
  background: #e2e8f0;
  color: #374151;
  border-radius: 16px;
  padding: 0.25rem 0.75rem;
  font-size: 0.95em;
  margin-right: 0.5rem;
  margin-bottom: 0.25rem;
}
.clear-chip-btn {
  background: none;
  border: none;
  color: #ff9f43;
  font-size: 1.1em;
  margin-left: 0.25rem;
  cursor: pointer;
  padding: 0 0.25rem;
  line-height: 1;
}
.applied-filters-clear-all {
  color: #ff9f43;
  font-size: 0.98em;
  margin-left: 0.5rem;
  text-decoration: underline;
}
.pagination-container {
  border-top: 1px solid #f1f5f9;
  padding-top: 1rem;
  background: #f8f9fa;
  border-radius: 0 0 20px 20px;
}
.pagination-info {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}
::v-deep .pagination .page-link {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-weight: 500;
  margin: 0 2px;
}
::v-deep .pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #B50100 0%, #ff4d4d 100%);
  border-color: #B50100;
  color: white;
}
::v-deep .pagination .page-link:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  color: #1e293b;
}
@media (max-width: 768px) {
  .modern-container {
    padding: 1rem;
  }
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  .simple-title {
    font-size: 1.5rem;
  }
  .stats-row {
    justify-content: center;
    gap: 1rem;
  }
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>
